import { useEffect, useRef } from 'react'

import type { LogVessel } from '../api'
import { playheadEpoch, type Playhead } from '../playhead'

/** Hvor båtene lå, sett ovenfra, uten kart.
 *
 * ---------------------------------------------------------------------------
 * HVORFOR IKKE SATELLITTKARTET
 *
 * Satellittkartet ble prøvd først og var tomt. En time med én båt 1,7 km unna
 * gir en ramme på 1,7 km åpent hav: et svart rektangel, et rødt punkt nede i
 * hjørnet, og en etikett klippet av kanten -- 210 piksler av sidens dyreste
 * plass brukt på å si det linja under den allerede sa. Flybildet fortjener
 * plassen sin først når båten var nær nok til at bildet viser noe, og det er
 * bare når den lå ved merdene.
 *
 * Denne ruta virker på alle avstander, trenger ingen fliser, og er lesbar om
 * natta -- der satellittbildet av en fjord i august er svart uansett.
 *
 * ---------------------------------------------------------------------------
 * HVA DEN VISER
 *
 * Anlegget i midten, nord opp, og hver båt der den faktisk lå: retning som
 * retning, avstand som avstand. Det er de to tallene som avgjør om en lyd kan
 * ha kommet derfra, og et tall som «1,7 km» bærer bare det ene av dem.
 *
 * Radien er LOGARITMISK, fra 10 m til 3 km. En lineær skala ville lagt
 * arbeidsbåten på 19 m oppå anlegget for at skipet på 1,7 km skulle få plass,
 * og 19 m mot 1,7 km er nettopp forskjellen ruta finnes for å vise -- mottatt
 * nivå faller med avstand, så to størrelsesordener i avstand er hele saken.
 */

/** Ringene. § 18 setter 500 m ferdselsforbud rundt et anlegg, og 2 km er så
 *  langt registerets spørring rekker -- så begge ringene er noe fra verden og
 *  ikke pene tall. 100 m er «ved merdene». */
const RINGER = [100, 500, 2000]
const INNERST_M = 10
const YTTERST_M = 3000

const grader = (x: number) => (x * Math.PI) / 180

/** Avstand i meter mellom to punkter. Haversine; på disse avstandene er
 *  jordkrumningen uvesentlig, men formelen er kortere enn unnskyldningen. */
function meter(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371000
  const dLat = grader(bLat - aLat)
  const dLon = grader(bLon - aLon)
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(grader(aLat)) * Math.cos(grader(bLat)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Rettvisende peiling fra anlegget til båten, i grader fra nord. */
function peiling(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLon = grader(bLon - aLon)
  const y = Math.sin(dLon) * Math.cos(grader(bLat))
  const x = Math.cos(grader(aLat)) * Math.sin(grader(bLat))
    - Math.sin(grader(aLat)) * Math.cos(grader(bLat)) * Math.cos(dLon)
  return (Math.atan2(y, x) * 180) / Math.PI
}

const LO = Math.log10(INNERST_M)
const HI = Math.log10(YTTERST_M)

/** Meter til radius, 0..1. Klemt i begge ender: en båt utenfor ytterste ring
 *  legges PÅ den framfor å forsvinne, fordi «lenger unna enn 3 km» er en
 *  opplysning og et tomt felt er ikke. */
function radius(m: number): number {
  const x = (Math.log10(Math.max(INNERST_M, Math.min(YTTERST_M, m))) - LO) / (HI - LO)
  return 0.1 + 0.9 * x
}

/** Posisjonen i sporet ved et gitt tidspunkt, interpolert mellom to meldinger.
 *
 *  Aldri ekstrapolert. Utenfor sporets ender returneres null, og båten faller
 *  tilbake til nærmeste passering -- en båt tegnet der AIS ikke sa den var, er
 *  en påstand vi ikke har dekning for. */
function iSporet(spor: [number, number, number][], t: number)
    : [number, number] | null {
  if (spor.length < 2) return null
  if (t < spor[0]![0] || t > spor[spor.length - 1]![0]) return null
  for (let i = 1; i < spor.length; i++) {
    const b = spor[i]!
    if (t <= b[0]) {
      const a = spor[i - 1]!
      const spenn = b[0] - a[0]
      const k = spenn > 0 ? (t - a[0]) / spenn : 0
      return [a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k]
    }
  }
  return null
}

export function Peiling({ senter, fartoy, ph, hoyde = 168 }: {
  senter: { lat: number; lon: number }
  fartoy: LogVessel[]
  /** Spilleren, når en finnes. Mens et klipp spiller, går båtene der de
   *  faktisk gikk, i sanntid -- ett sekund lyd er ett sekund bevegelse. */
  ph?: Playhead
  hoyde?: number
}) {
  const g = useRef<SVGGElement>(null)
  const klokke = useRef<SVGTextElement>(null)
  /** Merkene som kan flyttes, med sporet sitt. Skrevet under render, lest av
   *  animasjonsløkka. Sporet forhåndsregnes bevisst IKKE per sekund: en time
   *  ville blitt 3600 attributter per båt i DOM-en for et svar en lineær
   *  interpolasjon gir på et titalls flyttalloperasjoner. */
  const levende = useRef<{ el: SVGGElement | null; hvile: [number, number];
                           spor: [number, number, number][] }[]>([])

  const R = 78
  const midt = 84

  const merker = fartoy.map((v) => {
    const lat = v.closest_lat
    const lon = v.closest_lon
    if (lat == null || lon == null) return null
    const m = v.closest_m ?? meter(senter.lat, senter.lon, lat, lon)
    const b = peiling(senter.lat, senter.lon, lat, lon)
    const rr = radius(m) * R
    const x = midt + rr * Math.sin(grader(b))
    const y = midt - rr * Math.cos(grader(b))

    /* Sporet gjennom timen, som en tynn bue. Det er dette som skiller «lå her»
       fra «kom forbi»: to båter på samme avstand er ikke samme hendelse hvis
       den ene stod stille og den andre passerte. */
    const spor = (v.track ?? []).filter((p) => p[1] != null && p[2] != null)
    const bane = spor.map((p) => {
      const mm = meter(senter.lat, senter.lon, p[1], p[2])
      const bb = peiling(senter.lat, senter.lon, p[1], p[2])
      const r2 = radius(mm) * R
      return `${(midt + r2 * Math.sin(grader(bb))).toFixed(1)},${
        (midt - r2 * Math.cos(grader(bb))).toFixed(1)}`
    })

    return { v, x, y, m, bane }
  }).filter(Boolean) as {
    v: LogVessel; x: number; y: number; m: number; bane: string[]
  }[]

  /* Én løkke som flytter merkene, ikke en tilstandsoppdatering per bilde. Å
     rendre denne komponenten seksti ganger i sekundet for å flytte en prikk
     fire piksler er den vanligste måten en visualisering blir treg på.

     Interpolert mellom AIS-meldingene og aldri ekstrapolert: utenfor sporets
     ender står båten der den nærmeste passeringen var. En båt tegnet der AIS
     ikke sa den var, er en påstand vi ikke har dekning for -- og den ville
     vært usynlig som feil nettopp fordi den beveger seg troverdig. */
  useEffect(() => {
    if (!ph) return
    const rolig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (rolig) return
    let raf = 0
    const steg = () => {
      const t = playheadEpoch(ph)
      for (const l of levende.current) {
        if (!l?.el) continue
        let x = l.hvile[0]
        let y = l.hvile[1]
        const p = t === null ? null : iSporet(l.spor, t)
        if (p) {
          const rr = radius(meter(senter.lat, senter.lon, p[0], p[1])) * R
          const bb = peiling(senter.lat, senter.lon, p[0], p[1])
          x = midt + rr * Math.sin(grader(bb))
          y = midt - rr * Math.cos(grader(bb))
        }
        l.el.setAttribute('transform', `translate(${x.toFixed(1)} ${y.toFixed(1)})`)
      }
      if (klokke.current) {
        klokke.current.textContent = t === null ? ''
          : new Date(t * 1000).toLocaleTimeString('nb-NO',
              { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      }
      raf = requestAnimationFrame(steg)
    }
    raf = requestAnimationFrame(steg)
    return () => cancelAnimationFrame(raf)
  }, [ph, fartoy, senter.lat, senter.lon])

  /* Under effekten, ikke over: en tidlig retur før en hook gir React #300 i
     det øyeblikket fartøylisten går fra tom til ikke-tom eller omvendt. */
  if (!merker.length) return null

  /* Bare den nærmeste navngis. To etiketter i en rute på 168 piksler ligger
     oppå hverandre, og den nærmeste er den saken handler om. */
  const naermest = merker.reduce((a, b) => (b.m < a.m ? b : a))

  return (
    <div className="peiling" style={{ height: hoyde }}>
      <svg viewBox="0 0 168 178" role="img"
           aria-label={`${merker.length} fartøy, nærmeste ${
             naermest.m < 1000 ? `${Math.round(naermest.m)} meter`
               : `${(naermest.m / 1000).toFixed(1)} kilometer`}`}>
        {RINGER.map((m) => (
          <circle key={m} className="peiling-ring" cx={midt} cy={midt}
                  r={radius(m) * R} />
        ))}
        {/* Nord, som en strek og en bokstav. Uten den er en peiling en tegning. */}
        <line className="peiling-nord" x1={midt} y1={midt - R - 5}
              x2={midt} y2={midt - R + 3} />
        <text className="peiling-n" x={midt} y={9} textAnchor="middle">N</text>

        <g ref={g}>
          {merker.map(({ v, x, y, bane }, i) => (
            <g key={v.mmsi}>
              {bane.length > 1 && (
                <polyline className={`peiling-spor${v.aqua_fleet ? ' akva' : ''}`}
                          points={bane.join(' ')} />
              )}
              <g transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}
                 ref={(el) => { if (el) levende.current[i] = { el, hvile: [x, y],
                   spor: (v.track ?? [])
                     .filter((p) => p[1] != null && p[2] != null)
                     .map((p) => [p[0], p[1], p[2]] as [number, number, number]) } }}>
                <circle className={`peiling-baat${v.aqua_fleet ? ' akva' : ''}`}
                        r={v === naermest.v ? 4 : 3} />
              </g>
            </g>
          ))}
        </g>

        {/* Anlegget: samme røde punkt som ellers betyr «her er tingen selv». */}
        <circle className="peiling-anlegg" cx={midt} cy={midt} r={3.5} />

        <text className="peiling-navn" x={midt} y={172} textAnchor="middle">
          {naermest.v.name ?? naermest.v.mmsi}
          {' '}
          {naermest.m < 1000 ? `${Math.round(naermest.m)} m`
            : `${(naermest.m / 1000).toFixed(1)} km`}
        </text>
        <text className="peiling-klokke" ref={klokke} x={midt} y={162}
              textAnchor="middle" />
      </svg>
      {/* Ringene navngis én gang, ytterst, og ikke tre ganger. */}
      <span className="peiling-skala">100 m · 500 m · 2 km</span>
    </div>
  )
}
